import React, { Fragment, useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { getFieldPath, removeSelectOptionAndCombination } from "utils/form";
import get from "lodash/get";
import kebabCase from "lodash/kebabCase";
import DebounceInput from "components/DebounceInput";
import HoverIconButton from "components/HoverIconButton";
import { DEFAULT_DEBOUNCE } from "utils/constants/form";
import { COLORS } from "utils/styles";

const SelectOption = ({
  index,
  uuid,
  name,
  disabled,
  arrayHelpers,
  fieldName,
  combinations,
  advanced
}) => {
  const [firstMount, setFirstMount] = useState(false);
  const {
    values,
    touched,
    setFieldTouched,
    setFieldValue
  } = useFormikContext();

  const linkedPath = getFieldPath(name, "linked");
  const linkedValue = get(values, linkedPath, true);
  const linkedTouched = get(touched, linkedPath);

  const uidPath = getFieldPath(name, "uid");
  const uidValue = get(values, uidPath);
  const uidTouched = get(touched, uidPath);

  const [showAdvanced, setShowAdvanced] = useState(false);

  const labelPath = getFieldPath(name, "label");
  const labelValue = get(values, labelPath);
  const labelTouched = get(touched, labelPath);

  const tooltip = `Delete option`;

  useEffect(() => {
    if (firstMount) {
      setFirstMount(true);
    } else if (linkedValue) {
      const syncedUid = kebabCase(labelValue);

      setTimeout(() => setFieldValue(uidPath, syncedUid), 0);

      if (!uidTouched) {
        setFieldTouched(uidPath, true);
      }
    }
  }, [labelValue, linkedValue, firstMount]);

  const remove = removeSelectOptionAndCombination({
    arrayHelpers,
    setFieldValue,
    uuid,
    index,
    touched,
    name,
    fieldName,
    setFieldTouched,
    combinations
  });

  const uidProps = {
    inputValue: uidValue,
    placeholder: "Value",
    debounce: DEFAULT_DEBOUNCE,
    minRows: 1,
    name: uidPath,
    disabled,
    valueSet: (val) => {
      setFieldValue(uidPath, val);
      if (!uidTouched) {
        setFieldTouched(uidPath, true);
      }
    }
  };
  if (linkedValue) {
    uidProps.uid = uidValue;
    uidProps.disabled = true;
  }
  let deleteAction = (
    <HoverIconButton
      size={14}
      type="delete"
      tooltip={tooltip}
      onClick={remove}
    />
  );
  let showAdvancedAction = (
    <HoverIconButton
      size={14}
      onClick={() => setShowAdvanced(!showAdvanced)}
      color={showAdvanced ? COLORS.blue : COLORS.primary}
      type={showAdvanced ? "eye" : "eyeClosed"}
    />
  );
  /**
   * If advanced then show delete action below standard option field settings
   */
  let primaryAction = advanced ? showAdvancedAction : deleteAction;
  let secondaryAction = advanced ? deleteAction : null;

  const showAdvancedSettings = Boolean(advanced && showAdvanced);
  const labelInput = (
    <DebounceInput
      inputValue={labelValue}
      placeholder="Label"
      debounce={DEFAULT_DEBOUNCE}
      disabled={disabled}
      name={labelPath}
      valueSet={(val) => {
        console.log("DEBOUNCE INPUT");
        setFieldValue(labelPath, val);

        if (!labelTouched) {
          setFieldTouched(labelPath, true);
        }
      }}
    />
  );

  return (
    <Fragment>
      {showAdvancedSettings ? (
        <div className="w-100 flex flex-column">
          <div className="w-100 flex flex-row justify-between items-center">
            <label className="f6 dib fw6">Label</label>
            <div className="dib">{primaryAction}</div>
          </div>
          {labelInput}
        </div>
      ) : (
        <div className="flex flex-row items-center">
          {labelInput}
          <div className="dib pl2">{primaryAction}</div>
        </div>
      )}
      {showAdvancedSettings && (
        <div className="w-100 pt3">
          <div className="w-100 flex flex-row justify-between items-center">
            <label className="f6 dib fw6">Value</label>
            <div className="dib">
              <HoverIconButton
                size={14}
                onClick={() => {
                  setFieldValue(linkedPath, !linkedValue);
                  if (!linkedTouched) {
                    setFieldTouched(linkedPath, true);
                  }
                }}
                color={linkedValue ? COLORS.primary : COLORS.blue}
                tooltip={linkedValue ? "Unlink value" : "Link value"}
                type={linkedValue ? "linked" : "unlinked"}
              />
            </div>
          </div>
          <DebounceInput {...uidProps} />
        </div>
      )}
      {secondaryAction ? (
        <div className="SELECT pt3 self-end dib flex flex-row justify-between items-center">
          {secondaryAction}
        </div>
      ) : null}
    </Fragment>
  );
};

export default SelectOption;
